/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";

import type {
  CommandPaletteAgentItem,
  CommandPaletteNavigateItem,
  OrganizationMenuItem,
  ScopeSummary,
  TopBarContextAction,
} from "@marker/ui";
import {
  AgentSelector,
  AppShell,
  OrganizationMenu,
  PrimarySidebar,
  SidebarAgentSection,
  TeamSelector,
  ToastProvider,
  TopBar,
} from "@marker/ui";

import { getRuntimeConfig } from "../config";
import { documentationHref, SUPPORT_MAILTO } from "./helpLinks";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, isApiRequestError } from "../lib/errors";
import { useIsLicensed } from "../lib/licenseQuery";
import { DEFAULT_PAGE_SIZE, nextPageParam } from "../lib/pagination";
import {
  persistLastActiveScope,
  persistSidebarCollapsed,
  readLastActiveScope,
  readSidebarCollapsed,
} from "../lib/preferences";
import { queryKeys } from "../lib/queryKeys";
import { buildBreadcrumbs } from "../lib/shellBreadcrumbs";
import {
  buildScopeSummary,
  coordinatedAgentSwitchDestination,
  coordinatedTeamSwitchDestination,
  initials,
  liveShellNavEntries,
  parseShellPath,
  shellNavigationSearch,
  shortResourceId,
} from "../lib/shellNavigation";
import { useShellStream } from "../lib/sse";
import { useTypedNavigate } from "../lib/typedRouter";
import { fetchDashboards } from "../lib/useDashboardBoards";
import {
  ResolvedWorkingTeamIdContext,
  resolveWorkingTeamId,
  resolveWorkingTeamIdAfterMissingCandidate,
  useWorkingScope,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { buildSelectorItems } from "./agentSelectorItems";
import { LicenseBanner } from "./LicenseBanner";
import { SidebarNav } from "./shared/SidebarNav";
import { SidebarUsageRow } from "./SidebarUsageRow";
import { useShellTheme } from "./useShellTheme";
import { useScenarioBreadcrumbName } from "./useScenarioBreadcrumbName";
import type {
  ShellNavRoute,
  ShellNavScreenSearch,
} from "../lib/shellNavigation";
import type { components } from "../api/schema";

type Agent = components["schemas"]["AgentResponse"];
type AppLayoutSearch = WorkingScopeSearch &
  Partial<ShellNavScreenSearch> & { new?: 1 };

const firstPageToken: string | undefined = undefined;

function isStaleScopeResolutionError(error: unknown): boolean {
  if (!isApiRequestError(error)) {
    return false;
  }
  return (
    error.status.code === "NOT_FOUND" ||
    error.status.code === "PERMISSION_DENIED"
  );
}

// Root shell over docs/09 §4 and UI-LAW §8 shell IA. It owns navigation chrome;
// feature screens render through <Outlet /> with root-owned working scope.
export function AppLayout() {
  const location = useLocation();
  const nativeNavigate = useNavigate();
  const navigate = useTypedNavigate<AppLayoutSearch>();
  const queryClient = useQueryClient();
  const scope = useWorkingScope();
  const routeState = parseShellPath(location.pathname);
  const routeAgentId =
    routeState.area === "agent" ? routeState.agentId : undefined;
  const [isSidebarCollapsed, setIsSidebarCollapsed] =
    useState(readSidebarCollapsed);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isAgentSelectorOpen, setIsAgentSelectorOpen] = useState(false);
  const [isTeamSelectorOpen, setIsTeamSelectorOpen] = useState(false);
  const theme = useShellTheme();
  const teamCreateAuthority = useCallerAuthority({
    enabled: isTeamSelectorOpen,
  });
  const docsHref = useMemo(
    () => documentationHref(getRuntimeConfig().helpLinks),
    [],
  );
  // Read once per mount: the persisted last-active scope participates in the
  // fallback chain below; writes happen in the persistence effect.
  // (Destructured because the me-phantom-facet invariant scan bans `.team`
  // member reads in /v1/me-consuming files; this is a scope preference, not
  // a session claim.)
  const [{ team: persistedTeamId }, setPersistedScope] =
    useState(readLastActiveScope);

  const openCreateTeam = () => {
    setIsTeamSelectorOpen(false);
    void navigate({
      search: (prev: WorkingScopeSearch & { new?: 1 }) => ({
        ...shellNavigationSearch(
          "/settings/teams",
          location.pathname,
          routeAgentId,
        )(prev),
        new: 1 as const,
      }),
      to: "/settings/teams",
    });
  };

  // The authenticated session's own organization (docs/05 §1-§2). This is the
  // truthful current org — resolved from the session, not the first row of a
  // list read.
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // Same detail key as ScenarioRows, so this dedupes while the editor mounts.
  const scenarioName = useScenarioBreadcrumbName(routeState.scenarioId);

  // The active agent scope: the selector scope, or a direct /agents/$agentId
  // deep link.
  const visibleAgentScopeId = routeAgentId ?? scope.agentId;

  // The shell owns the single app-wide SSE subscription (docs/09 §5): the
  // selector scope drives `agent_id`; All agents = the team-wide stream
  // Gated on a confirmed session (/v1/me) so the OIDC handoff never opens an
  // unauthenticated stream.
  useShellStream(visibleAgentScopeId ?? null, me.isSuccess);
  const agent = useQuery({
    enabled: Boolean(visibleAgentScopeId),
    queryKey: queryKeys.agents.detail(visibleAgentScopeId ?? "pending"),
    queryFn: async () => {
      if (!visibleAgentScopeId) {
        throw new Error("Agent id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/agents/{id}", {
        params: { path: { id: visibleAgentScopeId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    retry: false,
  });

  // Teams power the team selector label + menu and the create-agent form, so
  // the list read is ALWAYS enabled.
  const teams = useInfiniteQuery({
    queryKey: queryKeys.teams.list(),
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/v1/teams", {
        params: {
          query: { page_size: DEFAULT_PAGE_SIZE, page_token: pageParam },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    getNextPageParam: nextPageParam,
  });
  const teamItems = useMemo(
    () => teams.data?.pages.flatMap((page) => page.items) ?? [],
    [teams.data],
  );

  // The scoped agent's own team (only once the detail read confirms it): the
  // second link in the working-team resolution chain.
  const scopeAgentTeamId =
    visibleAgentScopeId !== undefined && agent.data?.id === visibleAgentScopeId
      ? agent.data.team_id
      : undefined;

  // Working team resolution (docs/09 §4): the scoped agent's team →
  // `?team=` param → persisted last-active team → first membership team.
  const workingTeamId = resolveWorkingTeamId({
    agentTeamId: scopeAgentTeamId,
    teamIds: teamItems.map((team) => team.id),
    paramTeamId: scope.teamId,
    persistedTeamId,
  });

  // Board name for the dashboard breadcrumb. Same branched query key as the
  // Dashboard route's own list read (agent wins; All agents = the working
  // team, docs/09 §4), so on /dashboard/* this dedupes to zero extra requests.
  const dashboardsForBreadcrumb = useQuery({
    enabled:
      Boolean(routeState.dashboardId) &&
      (scope.agentId != null || workingTeamId !== undefined),
    queryKey:
      scope.agentId != null
        ? queryKeys.dashboards.list({ agent_id: scope.agentId })
        : queryKeys.dashboards.list({ team_id: workingTeamId }),
    queryFn: () =>
      fetchDashboards(
        scope.agentId != null
          ? { agentId: scope.agentId }
          : { teamId: workingTeamId },
      ),
  });
  const dashboardName = routeState.dashboardId
    ? dashboardsForBreadcrumb.data?.find(
        (board) => board.id === routeState.dashboardId,
      )?.display_name
    : undefined;

  // Label from the always-on list; a working team beyond the loaded pages
  // falls back to a targeted detail read.
  const workingTeamFromList = teamItems.find(
    (team) => team.id === workingTeamId,
  );
  const teamDetail = useQuery({
    enabled: workingTeamId !== undefined && workingTeamFromList === undefined,
    queryKey: queryKeys.teams.detail(workingTeamId ?? "pending"),
    queryFn: async () => {
      if (workingTeamId === undefined) {
        throw new Error("Team id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/teams/{id}", {
        params: { path: { id: workingTeamId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    retry: false,
  });
  const isStaleWorkingTeam =
    teamDetail.isError && isStaleScopeResolutionError(teamDetail.error);

  // The ⌘K palette searches across every visible agent (a cross-team jump
  // re-scopes to that agent; the shell resolves its team). Stale-team recovery
  // shares this query and waits for agent presence before redirecting.
  const paletteAgents = useInfiniteQuery({
    enabled: isCommandPaletteOpen || isStaleWorkingTeam,
    queryKey: queryKeys.agents.list(),
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/v1/agents", {
        params: {
          query: { page_size: DEFAULT_PAGE_SIZE, page_token: pageParam },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    getNextPageParam: nextPageParam,
  });

  const teamIdsWithAgents = useMemo(
    () =>
      new Set(
        paletteAgents.data?.pages.flatMap((page) =>
          page.items.map((item) => item.team_id),
        ) ?? [],
      ),
    [paletteAgents.data],
  );

  const fallbackWorkingTeamId =
    workingTeamId === undefined
      ? undefined
      : resolveWorkingTeamIdAfterMissingCandidate(
          {
            agentTeamId: scopeAgentTeamId,
            teamIds: teamItems.map((team) => team.id),
            teamIdsWithAgents,
            paramTeamId:
              scope.teamId === workingTeamId ? undefined : scope.teamId,
            persistedTeamId:
              persistedTeamId === workingTeamId ? undefined : persistedTeamId,
          },
          workingTeamId,
        );

  const teamDetailData = teamDetail.data;
  const workingTeamLabel =
    workingTeamFromList?.display_name ??
    (teamDetailData !== undefined && teamDetailData.id === workingTeamId
      ? teamDetailData.display_name
      : undefined);

  // Concrete agent routes carry the agent id in the path; root-owned scope
  // params are redundant there and must not duplicate the route identity.
  useEffect(() => {
    if (routeAgentId === undefined) {
      return;
    }
    if (scope.agentId === undefined && scope.teamId === undefined) {
      return;
    }
    void navigate({
      replace: true,
      search: (prev: WorkingScopeSearch) => ({
        ...prev,
        agent: undefined,
        team: undefined,
      }),
    });
  }, [navigate, routeAgentId, scope.agentId, scope.teamId]);

  // Agent implies team (docs/09 §4), but selected-agent URLs are canonical
  // with `?agent=` only. Once the agent detail confirms the parent team, drop
  // any redundant or mismatched `?team=` from old links.
  useEffect(() => {
    if (
      routeAgentId !== undefined ||
      scope.agentId === undefined ||
      scopeAgentTeamId === undefined ||
      scope.teamId === undefined
    ) {
      return;
    }
    void navigate({
      replace: true,
      search: (prev: WorkingScopeSearch) => ({ ...prev, team: undefined }),
    });
  }, [navigate, routeAgentId, scope.agentId, scope.teamId, scopeAgentTeamId]);

  // A persisted or deep-linked agent that no longer resolves clears the agent
  // scope instead of rendering a dead scope; the resolution chain above then
  // falls back for the team.
  useEffect(() => {
    if (scope.agentId === undefined || !agent.isError) {
      return;
    }
    if (!isStaleScopeResolutionError(agent.error)) {
      return;
    }
    void navigate({
      replace: true,
      search: (prev: WorkingScopeSearch) => ({ ...prev, agent: undefined }),
    });
  }, [agent.error, agent.isError, navigate, scope.agentId]);

  // A persisted or deep-linked team can outlive the local demo database or a
  // user's membership. Prefer a visible membership team with agents once
  // the shared org-wide query resolves; an empty/error result uses the first.
  useEffect(() => {
    if (workingTeamId === undefined || !isStaleWorkingTeam) {
      return;
    }
    if (
      (!paletteAgents.isSuccess && !paletteAgents.isError) ||
      teams.isPending
    ) {
      return;
    }
    if (persistedTeamId === workingTeamId) {
      persistLastActiveScope({});
      // Keep the resolved fallback through the render before navigation lands.
      setPersistedScope({ team: fallbackWorkingTeamId });
    }
    if (scope.teamId !== undefined && scope.teamId !== workingTeamId) {
      return;
    }
    void navigate({
      replace: true,
      // Replace stale URL scope with the next viable team instead of only
      // dropping the param.
      search: (prev: WorkingScopeSearch) => ({
        ...prev,
        team: fallbackWorkingTeamId,
      }),
    });
  }, [
    navigate,
    persistedTeamId,
    scope.teamId,
    isStaleWorkingTeam,
    paletteAgents.isError,
    paletteAgents.isSuccess,
    teams.isPending,
    fallbackWorkingTeamId,
    workingTeamId,
  ]);

  // Persist the last-active working scope on every scope change — a display
  // preference exactly like the theme choice (docs/09 §4).
  useEffect(() => {
    if (workingTeamId === undefined) {
      return;
    }
    persistLastActiveScope({ agent: visibleAgentScopeId, team: workingTeamId });
  }, [visibleAgentScopeId, workingTeamId]);

  // Chrome navigation carries canonical scope only: agent-selected product
  // routes get `?agent=`, All agents routes keep `?team=`, and team-bound
  // settings rows receive the resolved working team. A registry entry may add
  // the one screen param it owns on top (the palette's Templates row opens
  // /simulations on its Templates tab).
  const navigateTo = useCallback(
    (to: ShellNavRoute, screenSearch?: ShellNavScreenSearch) => {
      void navigate({
        search: (previous: AppLayoutSearch) => ({
          ...shellNavigationSearch(
            to,
            location.pathname,
            routeAgentId,
            workingTeamId,
          )(previous),
          ...screenSearch,
        }),
        to,
      });
    },
    [location.pathname, navigate, routeAgentId, workingTeamId],
  );

  // The sidebar selector lists the WORKING TEAM's agents (docs/09 §4);
  // fetched only while open.
  const agents = useInfiniteQuery({
    enabled:
      isAgentSelectorOpen &&
      workingTeamId !== undefined &&
      (workingTeamFromList !== undefined || teamDetail.isSuccess),
    queryKey: queryKeys.agents.list({ team_id: workingTeamId }),
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/v1/agents", {
        params: {
          query: {
            page_size: DEFAULT_PAGE_SIZE,
            page_token: pageParam,
            team_id: workingTeamId,
          },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    getNextPageParam: nextPageParam,
  });

  // One switch semantic (docs/09 §4): palette selection preserves the current
  // surface and re-scopes it. The sidebar selector's agent rows are the direct
  // entry point to the agent hub.
  const selectAgent = useCallback(
    (nextAgentId: string, nextTeamId?: string) => {
      const destination = coordinatedAgentSwitchDestination(
        routeState,
        nextAgentId,
      );
      if (destination?.to === "/agents/$agentId") {
        void navigate({
          params: destination.params,
          search: destination.search,
          to: destination.to,
        });
        return;
      }
      if (destination?.to === "/agents/$agentId/variables") {
        void navigate({
          params: destination.params,
          search: destination.search,
          to: destination.to,
        });
        return;
      }
      if (destination?.to === "/scenarios") {
        void navigate({
          search: destination.search,
          to: destination.to,
        });
        return;
      }
      scope.selectAgent(nextAgentId, nextTeamId);
    },
    [navigate, routeState, scope],
  );

  const openAgent = (nextAgentId: string, nextTeamId?: string) => {
    void nextTeamId;
    void navigate({
      params: { agentId: nextAgentId },
      search: {},
      to: "/agents/$agentId",
    });
  };

  const openAgentVariables = (nextAgentId: string) => {
    void navigate({
      params: { agentId: nextAgentId },
      search: {},
      to: "/agents/$agentId/variables",
    });
  };

  const copyAgentId = (nextAgentId: string) => {
    if (typeof window === "undefined") {
      return;
    }
    void window.navigator.clipboard.writeText(nextAgentId);
  };

  const clearAgent = () => {
    if (routeState.scenarioId !== undefined) {
      void navigate({
        search: workingTeamId === undefined ? {} : { team: workingTeamId },
        to: "/scenarios",
      });
      return;
    }
    if (routeState.area === "agent" && routeState.agentId) {
      void navigate({
        search: workingTeamId === undefined ? {} : { team: workingTeamId },
        to: "/agent",
      });
      return;
    }
    scope.clearAgent(workingTeamId);
  };

  // Team change keeps the agent only when it belongs to the new team
  // (docs/09 §4 — a mismatched agent is dropped, never left diverged).
  const selectTeam = (nextTeamId: string) => {
    const destination = coordinatedTeamSwitchDestination(
      routeState,
      nextTeamId,
    );
    if (destination) {
      void navigate(destination);
      return;
    }
    scope.selectTeam(nextTeamId, scopeAgentTeamId);
  };

  const createAgent = useMutation({
    mutationFn: async (input: { displayName: string; teamId: string }) => {
      const { data, error } = await apiClient.POST("/v1/agents", {
        body: { display_name: input.displayName, team_id: input.teamId },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (created) => {
      setIsAgentSelectorOpen(false);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.agents.all,
      });
      selectAgent(created.id, created.team_id);
    },
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsCommandPaletteOpen(true);
      }
      if (event.key === "Escape") {
        setIsCommandPaletteOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const agentLabel =
    agent.data?.display_name ??
    (visibleAgentScopeId
      ? `Agent ${shortResourceId(visibleAgentScopeId)}`
      : "Agent");
  const activeAgentTeamId = agent.data?.team_id ?? workingTeamId;
  const topBarAgentLabel = visibleAgentScopeId ? agentLabel : "All agents";
  const topBarAgentActions: TopBarContextAction[] = visibleAgentScopeId
    ? [
        {
          icon: "pencil",
          label: "Edit agent",
          onSelect: () => {
            openAgent(visibleAgentScopeId, activeAgentTeamId);
          },
        },
        {
          icon: "settings",
          label: "Variables",
          onSelect: () => {
            openAgentVariables(visibleAgentScopeId);
          },
        },
        {
          icon: "copy",
          label: "Copy agent ID",
          onSelect: () => {
            copyAgentId(visibleAgentScopeId);
          },
        },
      ]
    : [];
  const scopeSummary = useMemo<ScopeSummary>(
    () =>
      buildScopeSummary({
        isPending: me.isPending,
        organizationName: me.data?.organization.name ?? null,
      }),
    [me.data, me.isPending],
  );

  const accountDisplayName = me.data?.user.display_name?.trim() ?? "";
  const accountEmail = me.data?.user.email;
  const accountLabel =
    accountDisplayName.length > 0
      ? accountDisplayName
      : (accountEmail ?? "Account");
  const accountInitials =
    accountDisplayName.length > 0
      ? initials(accountDisplayName)
      : initials(accountLabel);

  const breadcrumbs = buildBreadcrumbs({
    dashboardName,
    navigate: nativeNavigate,
    routeState,
    scenarioName,
  });

  const { agentSelectorItems, teamSelectorItems } = buildSelectorItems(
    agents.data?.pages,
    teamItems,
  );
  // Navigate group = LIVE registry routes (Soon + capability-gated excluded).
  const hostedGate = me.data?.credit_billing_enabled === true;
  const numbersGate = me.data?.number_provisioning_enabled === true;
  const licensedGate = useIsLicensed();
  const commandPaletteNavigateItems = useMemo<CommandPaletteNavigateItem[]>(
    () =>
      liveShellNavEntries({
        includeHostedOnly: hostedGate,
        includeLicensed: licensedGate,
        includeNumberProvisioning: numbersGate,
      }).map((entry) => ({
        id: entry.id,
        label: entry.label,
        onSelect: () => {
          navigateTo(entry.to, entry.search);
        },
      })),
    [hostedGate, licensedGate, navigateTo, numbersGate],
  );
  // Palette agent rows PRESERVE the current surface and re-scope it in place
  // (docs/09 §4 one-switch semantic) — never a hard navigation elsewhere.
  const paletteAgentPages = paletteAgents.data;
  const commandPaletteAgentItems = useMemo<CommandPaletteAgentItem[]>(() => {
    const items = paletteAgentPages?.pages.flatMap((page) => page.items) ?? [];

    return items.map((item: Agent) => ({
      displayName: item.display_name,
      id: item.id,
      onSelect: () => {
        selectAgent(item.id, item.team_id);
      },
      shortId: shortResourceId(item.id),
    }));
  }, [paletteAgentPages, selectAgent]);
  // The organization menu collapses to the current-org identity row plus ONE
  // Settings entry → /settings/organization (docs/09 §4); the full settings
  // row list lives in the settings shell's own sidebar.
  const organizationMenuItems = useMemo<OrganizationMenuItem[]>(
    () => [
      {
        icon: "settings",
        isActive: routeState.area === "settings",
        label: "Settings",
        onSelect: () => {
          navigateTo("/settings/organization");
        },
      },
    ],
    [navigateTo, routeState.area],
  );

  return (
    <ToastProvider>
      <AppShell
        commandPalette={{
          agentItems: commandPaletteAgentItems,
          hasNextAgentPage: paletteAgents.hasNextPage,
          isAgentsError: paletteAgents.isError,
          isAgentsPending: paletteAgents.isPending,
          navigateItems: commandPaletteNavigateItems,
        }}
        commandPaletteOpen={isCommandPaletteOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        onCloseCommandPalette={() => {
          setIsCommandPaletteOpen(false);
        }}
        sidebar={
          <PrimarySidebar
            isCollapsed={isSidebarCollapsed}
            onToggleCollapsed={() => {
              const next = !isSidebarCollapsed;
              persistSidebarCollapsed(next);
              setIsSidebarCollapsed(next);
            }}
            teamSelector={
              <TeamSelector
                activeTeamId={workingTeamId}
                activeTeamLabel={workingTeamLabel}
                canCreateTeam={teamCreateAuthority.canManage}
                hasNextPage={teams.hasNextPage}
                isError={teams.isError}
                isOpen={isTeamSelectorOpen}
                isPending={teams.isPending && !teams.isError}
                onCreateTeam={openCreateTeam}
                onLoadMore={() => {
                  void teams.fetchNextPage();
                }}
                onOpenChange={setIsTeamSelectorOpen}
                onSelectTeam={selectTeam}
                teams={teamSelectorItems}
              />
            }
            agentBlock={
              <SidebarAgentSection>
                <AgentSelector
                  activeAgentId={visibleAgentScopeId}
                  activeAgentLabel={
                    visibleAgentScopeId ? agentLabel : undefined
                  }
                  agents={agentSelectorItems}
                  createError={
                    createAgent.isError ? createAgent.error.message : null
                  }
                  defaultTeamId={workingTeamId}
                  hasNextPage={agents.hasNextPage}
                  isCreating={createAgent.isPending}
                  isError={agents.isError}
                  isOpen={isAgentSelectorOpen}
                  isPending={
                    isAgentSelectorOpen && agents.isPending && !agents.isError
                  }
                  isTeamsError={isAgentSelectorOpen && teams.isError}
                  isTeamsPending={
                    isAgentSelectorOpen && teams.isPending && !teams.isError
                  }
                  onCreateAgent={(input) => {
                    createAgent.mutate(input);
                  }}
                  onCopyAgentId={copyAgentId}
                  onLoadMore={() => {
                    void agents.fetchNextPage();
                  }}
                  onOpenAgent={openAgent}
                  onOpenAgentVariables={openAgentVariables}
                  onOpenChange={setIsAgentSelectorOpen}
                  onOpenArchivedAgents={() => {
                    navigateTo("/settings/agents");
                  }}
                  onSelectAllAgents={clearAgent}
                  teams={teamSelectorItems}
                />
              </SidebarAgentSection>
            }
            footer={
              // Sidebar footer anatomy (UI-LAW §8): the usage row sits ABOVE
              // the organization menu; the collapsed rail hides it (CSS).
              <>
                <SidebarUsageRow
                  onOpenUsageSettings={() => {
                    navigateTo("/settings/usage");
                  }}
                />
                <OrganizationMenu
                  {...scopeSummary}
                  menuItems={organizationMenuItems}
                />
              </>
            }
          >
            <SidebarNav area={routeState.area} onNavigate={navigateTo} />
          </PrimarySidebar>
        }
        topBar={
          <TopBar
            accountEmail={accountEmail}
            accountInitials={accountInitials}
            accountLabel={accountLabel}
            breadcrumbs={breadcrumbs}
            contextActions={topBarAgentActions}
            contextLabel={topBarAgentLabel}
            contextMenuLabel={
              visibleAgentScopeId ? `${topBarAgentLabel} actions` : undefined
            }
            docsHref={docsHref}
            onOpenContext={
              visibleAgentScopeId
                ? () => {
                    openAgent(visibleAgentScopeId, activeAgentTeamId);
                  }
                : undefined
            }
            onOpenProfile={() => {
              navigateTo("/settings/profile");
            }}
            onOpenSearch={() => {
              setIsCommandPaletteOpen(true);
            }}
            onSelectThemePreference={theme.select}
            supportHref={SUPPORT_MAILTO}
            themePreference={theme.preference}
          />
        }
      >
        <ResolvedWorkingTeamIdContext.Provider value={workingTeamId}>
          <LicenseBanner />
          <Outlet />
        </ResolvedWorkingTeamIdContext.Provider>
      </AppShell>
    </ToastProvider>
  );
}
